<template>
    <div id="main">
        <loader v-if="isLoading" />
        <div class="page page-container">
            <!-- CONTAINER DE ALERTAS -->
            <alert-container :messages="msgs" />

            <div class="vertical-flow">
                <div class="listing-header mb-3">
                    <div class="title-breadcrumb">
                        <h1 class="title">
                            {{ $t('adicionar-desconto-qtde') }}
                        </h1>
                    </div>

                    <!-- BOTÃO DE SALVAR -->
                    <div class="header-buttons">
                        <button class="delete-btn" type="button" @click="fecharModal()">
                            {{ $t('cancelar') }}
                        </button>
                        <button
                            v-if="(!isUpdate && permissao.create) || (isUpdate && permissao.update)"
                            class="save-btn"
                            type="button"
                            @click="save"
                            :disabled="isSaving"
                        >
                            {{ $t('salvar') }}
                            <button-loader :isSaving="isSaving" />
                        </button>
                    </div>
                </div>
                <br />
                <div class="form-columns-fit">
                    <div class="columns is-flex-wrap">
                        <!-- SELEÇÃO DE EMPRESA -->
                        <div class="column is-12">
                            <vss-select
                                :id="generateId(10)"
                                ref="configEmpresaInput"
                                v-model="empresaSelected"
                                title="empresa"
                                placeholder="msg-selecione-empresa"
                                :required="true"
                                :invalid="empresaSelected == null"
                                :options="empresasSelect"
                                @input="empresaSelecionada"
                                tooltipLabel="msg-empresa-com-tab-preco-com-produto"
                                tooltipSize="is-small"
                                tooltipPosition="is-right"
                            />
                        </div>
                    </div>
                </div>
                <br />
                <div class="form-columns-fit">
                    <div class="columns is-flex-wrap">
                        <div class="column is-4">
                            <v-input
                                :id="generateId(22)"
                                ref="idErpInput"
                                v-model="descontoQuantidade.idprodutoqtdeprecoerp"
                                placeholder="placeholder-preencha"
                                :maxlength="30"
                                title="codigo"
                                required
                                tooltipLabel="cod-ref"
                                :invalid="!descontoQuantidade.idprodutoqtdeprecoerp"
                                uppercase
                            />
                        </div>

                        <!-- SELEÇÃO DE TABELA DE PREÇO -->
                        <div class="column is-4">
                            <vss-select
                                :id="generateId(20)"
                                ref="configTabPrecoInput"
                                v-model="tabelaPrecoSelected"
                                title="tab-preco"
                                placeholder="msg-selecione-tab-preco"
                                :required="true"
                                :invalid="tabelaPrecoSelected == null"
                                invalidWhenDisabled
                                :options="tabelasPrecoSelect"
                                :disabled="desativarCampoTabelasPreco"
                                @input="tabelaPrecoSelecionada"
                                tooltipLabel="msg-tab-preco-com-produto"
                                tooltipSize="is-small"
                                tooltipPosition="is-right"
                                :loading="tabprecoLoading"
                            />
                        </div>

                        <!-- SELEÇÃO DE PRODUTO -->
                        <div class="column is-4">
                            <vss-select
                                :id="generateId(30)"
                                ref="configProdutoInput"
                                v-model="produtoSelected"
                                title="produto"
                                placeholder="msg-selecione-produto"
                                :required="true"
                                :invalid="produtoSelected == null"
                                :options="produtosSelect"
                                :disabled="desativarCampoProdutos || produtosSelect.length == 0"
                                @input="desativarCamposNumericos = false"
                                @search="carregarListaProdutos"
                                searchable
                                :loading="produtosLoading"
                                invalidWhenDisabled
                                tooltipLabel="msg-produto-de-tab-preco"
                                tooltipSize="is-small"
                                tooltipPosition="is-top"
                            />
                        </div>
                    </div>
                </div>
                <br />

                <div class="form-columns-fit">
                    <div class="columns is-flex-wrap">
                        <!-- QUANTIDADE INICIAL -->
                        <div class="column is-3">
                            <v-money
                                :id="generateId(40)"
                                ref="qtdIni"
                                v-model="descontoQuantidade.qtdeinicio"
                                title="qtd-ini"
                                tooltipLabel="msg-tooltip-qtde-inicial"
                                tooltipSize="is-small"
                                :required="true"
                                :disabled="desativarCamposNumericos"
                                :msg="invalidQtdeinicioMsg"
                                :invalid="invalidQtdeinicio"
                                @onBlurFunction="validateQtdeinicio()"
                                :min="0"
                                :max="99999999.99"
                            />
                        </div>

                        <!-- QUANTIDADE FINAL -->
                        <div class="column is-3">
                            <v-money
                                :id="generateId(50)"
                                ref="qtdFim"
                                v-model="descontoQuantidade.qtdefim"
                                title="qtd-fim"
                                tooltipLabel="msg-tooltip-qtde-final"
                                tooltipSize="is-small"
                                :disabled="desativarCamposNumericos"
                                :required="true"
                                :msg="invalidQtdefimMsg"
                                :invalid="invalidQtdefim"
                                @onBlurFunction="validateQtdefim()"
                                :min="0"
                                :max="99999999.99"
                            />
                        </div>

                        <!-- DIFERENÇA -->
                        <div class="column is-3">
                            <v-money
                                :id="generateId(60)"
                                ref="diferenca"
                                class="height-36"
                                v-model="descontoQuantidade.diferenca"
                                title="diferenca"
                                tooltipLabel="msg-tooltip-diferenca"
                                tooltipSize="is-small"
                                :required="true"
                                :disabled="desativarCamposNumericos"
                                :msg="invalidDiferencaMsg"
                                :invalid="invalidDiferenca"
                                @onBlurFunction="validateDiferenca()"
                            />
                        </div>

                        <!-- DESCONTO MÁXIMO -->
                        <div class="column is-3">
                            <v-money
                                :id="generateId(70)"
                                class="height-36"
                                ref="descMax"
                                v-model="descontoQuantidade.descmaximo"
                                title="desc-max"
                                tooltipLabel="msg-tooltip-desc-max"
                                tooltipSize="is-small"
                                tooltipPosition="is-left"
                                :required="true"
                                :disabled="desativarCamposNumericos"
                                :msg="invalidDescmaximoMsg"
                                :invalid="invalidDescmaximo"
                                @onBlurFunction="validateDescmaximo()"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
//SERVICE
import ProdutoQtdePrecoService from '@/services/vendas/cliente/abas/ProdutoQtdePrecoService';
import VenProdutoService from '@/services/vendas/produto/VenProdutoService';

//VALIDATION
import DescontoQuantidadeValidation from '@/services/validations/vendas/DescontoQuantidadeValidation';

//COMPONENTS
import HideInfo from '@/components/shared/HideInfo';

//UTILS
import Message from '@/models/Message';
import { mapState } from 'vuex';

export default {
    components: {
        HideInfo,
    },
    props: {
        idCliente: 0,
    },
    data() {
        return {
            validation: new DescontoQuantidadeValidation(this.$root.msgs),
            service: new ProdutoQtdePrecoService(this.$http, this.$router),
            precoService: new VenProdutoService(this.$http, this.$router),
            isLoading: false,
            msgs: [],
            isSaving: false,
            descontoQuantidade: {
                idprodutoqtdeprecoerp: null,
                idcliente: null,
                idproduto: null,
                idtabpreco: null,
                idempresa: null,
                qtdeinicio: 0,
                qtdefim: 0,
                diferenca: 0,
                descmaximo: 0,
                situacao: 1,
            },
            isUpdate: false,

            //listas para receber valores das combobox
            empresasSelect: [],
            tabelasPrecoSelect: [],
            produtosSelect: [],

            //variáveis para armazenar os valores selecionados nas combobox
            produtoSelected: null,
            empresaSelected: null,
            tabelaPrecoSelected: null,

            //booleanos para desabilitar campos que necessitam de seleção prévia de outros campos, iniciam desativados
            desativarCampoTabelasPreco: true,
            desativarCampoProdutos: true,
            desativarCamposNumericos: true,

            //booleano para exibir mensagem ao usuário informando-o que a tabela selecionada não se relaciona com o cliente
            asstabprecocliente: false,

            produtosLoading: false,
            invalidQtdeinicio: true,
            invalidQtdeinicioMsg: 'msg-campo-obrigatorio',
            invalidQtdefim: true,
            invalidQtdefimMsg: 'msg-campo-obrigatorio',
            invalidDiferenca: true,
            invalidDiferencaMsg: 'msg-campo-obrigatorio',
            invalidDescmaximo: true,
            invalidDescmaximoMsg: 'msg-campo-obrigatorio',
            tabprecoLoading: false,

            isVenda: this.$route.meta.contexto == 'vendas',
        };
    },
    created() {
        this.permissao = this.hasPermissionAccess(
            'vendas',
            'clientes',
            'ven-cliente-politica-comercial-desconto-qtde'
        );
        if (!this.permissao.create) {
            this.$router.push({
                name: 'acesso-nao-permitido',
                params: { tela: 'cliente' },
            });
        }
        this.carregarListaEmpresas();
    },
    computed: {
        ...mapState(['hide']),
    },
    methods: {
        generateId(numberId) {
            return this.$route.name + '-desconto-qtde-' + numberId;
        },
        validateQtdeinicio() {
            if (this.isNull(this.descontoQuantidade.qtdeinicio)) {
                this.invalidQtdeinicio = true;
                this.invalidQtdeinicioMsg = 'msg-campo-obrigatorio';
            } else if (!this.validateQuantidade(+this.descontoQuantidade.qtdeinicio)) {
                this.invalidQtdeinicio = true;
                this.invalidQtdeinicioMsg = 'msg-campo-invalido';
            } else {
                this.invalidQtdeinicio = false;
            }
        },
        validateQtdefim() {
            if (this.isNull(this.descontoQuantidade.qtdefim)) {
                this.invalidQtdefim = true;
                this.invalidQtdefimMsg = 'msg-campo-obrigatorio';
            } else if (!this.validateQuantidade(+this.descontoQuantidade.qtdefim)) {
                this.invalidQtdefim = true;
                this.invalidQtdefimMsg = 'msg-campo-invalido';
            } else if (+this.descontoQuantidade.qtdefim <= +this.descontoQuantidade.qtdeinicio) {
                this.invalidQtdefim = true;
                this.invalidQtdefimMsg = 'msg-erro-desc-qtd-ini-fim';
            } else {
                this.invalidQtdefim = false;
            }
        },
        validateDiferenca() {
            if (this.isNull(this.descontoQuantidade.diferenca)) {
                this.invalidDiferenca = true;
                this.invalidDiferencaMsg = 'msg-campo-obrigatorio';
            } else if (!this.validatePorcentagem(+this.descontoQuantidade.diferenca)) {
                this.invalidDiferenca = true;
                this.invalidDiferencaMsg = 'msg-porcentagem-invalida';
            } else {
                this.invalidDiferenca = false;
            }
        },
        validateDescmaximo() {
            if (this.isNull(this.descontoQuantidade.descmaximo)) {
                this.invalidDescmaximo = true;
                this.invalidDescmaximoMsg = 'msg-campo-obrigatorio';
            } else if (!this.validatePorcentagem(+this.descontoQuantidade.descmaximo)) {
                this.invalidDescmaximo = true;
                this.invalidDescmaximoMsg = 'msg-porcentagem-invalida';
            } else {
                this.invalidDescmaximo = false;
            }
        },
        validateQuantidade(valor) {
            return valor > 0 && valor <= 99999999.99;
        },
        carregarListaEmpresas() {
            //popula caixa de seleção de empresa com valores obtidos do back
            this.service
                .loadEmpresas(this.$route.params.id)
                .then((body) => {
                    return body.content.map((m) => {
                        return {
                            id: m.idempresa,
                            label: m.nome,
                        };
                    });
                })
                .then((r) => {
                    this.empresasSelect = r;
                })
                .catch((e) => {
                    console.error('Erro ao carregar empresas.', e);
                    this.$root.showMessage('error', tempFailure);
                });
        },
        empresaSelecionada() {
            /* Após seleção de empresa, limpa os demais campos de seleção, desabilita os campos subsequentes e carrega
      a lista de tabelas de preço disponível para a empresa selecionada */

            //limpa listas antes de fazer nova busca
            this.tabelasPrecoSelect = [];

            //limpa próximos campos de seleção
            this.tabelaPrecoSelected = null;
            this.produtoSelected = null;

            // ativa o campo de tabelas de preço (próximo campo de seleção) e, por garantia, desativa os campos subsequentes
            this.desativarCampoTabelasPreco = false;
            this.desativarCampoProdutos = true;
            this.desativarCamposNumericos = true;

            this.carregarListaTabelasPreco();
        },
        carregarListaTabelasPreco() {
            //popula caixa de seleção de tabela de preços com valores obtidos do back

            this.service
                .loadTabelasPreco(this.$route.params.id, this.empresaSelected.id)
                .then((body) => {
                    return body.content.map((m) => {
                        return {
                            id: m.idtabpreco,
                            label: m.descricao,
                            asstabprecocliente: m.asstabprecocliente,
                        };
                    });
                })
                .then((r) => {
                    this.asstabprecocliente = r.length > 0 ? r[0].asstabprecocliente : false;
                    this.tabelasPrecoSelect = r;
                })
                .catch((e) => {
                    console.error('Erro ao carregar tabelas de preço.', e);
                    this.$root.showMessage('error', tempFailure);
                });
        },
        tabelaPrecoSelecionada() {
            //limpa listas antes de fazer nova busca
            this.produtosSelect = [];

            //limpa próximo campo de seleção
            this.produtoSelected = null;

            // ativa o campo de produtos (próximo campo de seleção) e, por garantia, desativa os campos subsequentes
            this.desativarCampoProdutos = false;
            this.desativarCamposNumericos = true;

            if (!this.asstabprecocliente) {
                this.msgs.push(
                    new Message('alert', 'msg-alerta-associacao-tabela-preco-e-cliente', true)
                );
            }

            this.carregarListaProdutos();
        },
        carregarListaProdutos(search = '') {
            if (this.tabelaPrecoSelected != null) {
                //popula caixa de seleção de produtos com valores obtidos do back
                this.produtosLoading = true;
                this.service
                    .loadProdutos(this.empresaSelected.id, this.tabelaPrecoSelected.id, search)
                    .then((body) => {
                        return body.content.map((m) => {
                            return {
                                id: m.idproduto,
                                label: m.descricao,
                            };
                        });
                    })
                    .then((r) => {
                        this.produtosSelect = r;
                    })
                    .catch((e) => {
                        console.error('Erro ao carregar produtos.', e);
                        this.$root.showMessage('error', tempFailure);
                    })
                    .finally(() => {
                        this.produtosLoading = false;
                    });
            }
        },
        save() {
            this.isLoading = true;

            let descontoQuantidadeTemp = Object.assign({}, this.descontoQuantidade);
            descontoQuantidadeTemp.idproduto =
                this.produtoSelected == null ? null : this.produtoSelected.id;
            descontoQuantidadeTemp.idtabpreco =
                this.tabelaPrecoSelected == null ? null : this.tabelaPrecoSelected.id;
            descontoQuantidadeTemp.idempresa =
                this.empresaSelected == null ? null : this.empresaSelected.id;

            if (this.validation.validate(descontoQuantidadeTemp, this.$refs)) {
                descontoQuantidadeTemp = this.mapTosave(descontoQuantidadeTemp);
                descontoQuantidadeTemp.idcliente = this.$route.params.id;

                if (
                    +descontoQuantidadeTemp.diferenca == 0 &&
                    +descontoQuantidadeTemp.descmaximo == 0
                ) {
                    this.msgs.push(
                        new Message('alert', 'msg-preencha-diferenca-ou-desconto', true)
                    );
                    this.isLoading = false;
                    return;
                }

                this.isSaving = true;
                this.saveDQ(descontoQuantidadeTemp)
                    .then((value) => {
                        if (value.message == null) {
                            let msg = 'msg-save-desconto-qtde';
                            this.msgs.push(new Message('success', msg, true));
                            this.$emit('fecharModal', true);
                            this.$root.showMessage(
                                'success',
                                'msg-sucesso-ao-adicionar-desconto-qtde'
                            );
                        } else {
                            if (this.$t(value.message)) {
                                this.msgs.push(new Message('alert', value.message, true));
                            } else {
                                this.msgs.push(
                                    new Message('error', 'msg-problema-realizar-acao', true)
                                );
                            }
                        }
                    })
                    .catch((e) => {
                        if(e.status === 409) {
                            this.msgs.push(new Message('warn', e.data.message, true));    
                        } else {
                            console.error('Erro ao salvar.', e);
                            this.msgs.push(new Message('error', 'msg-erro-load', true));
                        }
                    })
                    .finally(() => {
                        this.isSaving = false;
                        this.isLoading = false;
                    });
            } else {
                this.msgs.push(
                    new Message('alert', 'msg-preencher-campos-obrigatorios-corretamente', true)
                );
                this.isLoading = false;
            }
        },
        saveDQ(descontoQuantidade) {
            return this.$axios.post(`${this.isVenda ? 'ven' : 'agro'}/cliente/produtoqtdepreco`, descontoQuantidade);
        },
        fecharModal() {
            this.$emit('fecharModal', true);
        },
    },
};
</script>
<style lang="scss" scoped>
#main {
    padding: 0 20px;
    height: auto;

    .page {
        position: relative;
    }
    #color {
        padding: 0 !important;
        height: 36px;
        cursor: pointer;
    }
}
</style>
